import { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import type { QuizMetadata } from '@/types/quiz';
import { useQuizMetadataQuery } from '@/hooks/queries/useQuizMetadataQuery';
import { useQuizQuestionsQuery } from '@/hooks/queries/useQuizQuestionsQuery';
import type { QuizQuestion as QueryQuizQuestion } from '@/lib/services/questions';
import { useQuizCompletionQuery } from '@/hooks/queries/useQuizCompletionQuery';
import { useAuthId } from '@/hooks/useAuthId';
import { getActiveRunIdentityState, useActiveRunIdentity } from '@/lib/auth/activeRunIdentity';
import { useQueryClient } from '@tanstack/react-query';
import type { Question, QuizQuestion } from '../types';
import {
  createPreviewQuizRunPersistence,
  createQuizRunPersistence,
  isExpectedQuizRunPersistenceRetirement,
  type CanonicalItem,
  type QuizCompletion as DurableQuizCompletion,
  type QuizRunPersistence,
  type QuizRunPersistenceSnapshot,
} from '../quiz-run-persistence';
import { createQuizRunPersistenceTransport } from '../quiz-run-persistence/transport';
import { markGameRunCachesDirty, reconcileGameRunCaches } from '../game-run/cacheReconciliation';
import { isPollItem, isTriviaItem } from '../question-items/registry';
import { POLL_PARTICIPATION_XP } from '../run-mechanics';
import type { PartnerAnalyticsSession } from '@/lib/partnerAnalytics/sessionManager';
import type { PollResponseResult } from '@/lib/services/pollResponses';
import { socialLeaderboardKeys } from '@/lib/domain/socialLeaderboards';
import type {
  BaseQuestionsResult,
  LogAnswerData,
  QuizGameAdapter,
} from '../game-adapters.contract';

const QUIZ_PERSISTED_XP_MAX = 140;

type LocalQuizCompletion = {
  emoji_results: string;
  accuracy: number;
  total_questions: number;
  xp_earned?: number;
};

const getHydratedQuestionXp = (
  question: QuizQuestion | undefined,
  trackedXp: number | undefined
) => {
  if (typeof trackedXp === 'number') {
    return trackedXp;
  }
  if (typeof question?.xp_earned === 'number') {
    return question.xp_earned;
  }
  if (isPollItem(question) && question?.is_answered) {
    return POLL_PARTICIPATION_XP;
  }
  return 0;
};

const computeQuizXpTotal = (questions: QuizQuestion[], questionXp: number[]) =>
  questions.reduce(
    (sum, question, idx) => sum + getHydratedQuestionXp(question, questionXp[idx]),
    0
  );

const resolveQuizCompletionFacts = (
  questions: QuizQuestion[],
  trackedGuessSequences: number[][],
  trackedQuestionXp: number[],
  fallbackTotalQuestions = 0
) => {
  const totalQuestions = questions.length || fallbackTotalQuestions;
  const guessSequences = Array.from({ length: totalQuestions }, (_, idx) => {
    const hydratedSequence = questions[idx]?.guess_sequence;
    if (Array.isArray(hydratedSequence) && hydratedSequence.length > 0) {
      return hydratedSequence;
    }
    return Array.isArray(trackedGuessSequences[idx]) ? trackedGuessSequences[idx] : [];
  });

  let currentStreak = 0;
  let longestStreak = 0;
  guessSequences.forEach((sequence, index) => {
    if (isPollItem(questions[index])) return;
    currentStreak = sequence.length === 1 && sequence[0] === 0 ? currentStreak + 1 : 0;
    longestStreak = Math.max(longestStreak, currentStreak);
  });

  return {
    guessSequences,
    totalQuestions,
    xpEarned: computeQuizXpTotal(questions, trackedQuestionXp),
    longestStreak,
  };
};

const computeLocalQuizCompletion = (
  guessSequences: number[][],
  totalQuestions: number,
  questions: QuizQuestion[] = [],
  questionXp: number[] = []
): LocalQuizCompletion | null => {
  if (totalQuestions <= 0) return null;

  const hasProgress = Array.from({ length: totalQuestions }, (_, idx) => {
    const question = questions[idx];
    if (isPollItem(question)) {
      return !!question?.is_answered || (questionXp[idx] ?? 0) > 0;
    }
    const sequence = guessSequences[idx];
    return Array.isArray(sequence) && sequence.length > 0;
  }).some(Boolean);

  if (!hasProgress) return null;

  const emojiResults = Array.from({ length: totalQuestions }, (_, idx) => {
    const question = questions[idx];
    if (isPollItem(question)) {
      return '📊';
    }
    const sequence = guessSequences[idx];
    const attemptCount = Array.isArray(sequence) ? sequence.length : 0;
    const eventuallyCorrect = Array.isArray(sequence) && sequence.includes(0);
    if (!eventuallyCorrect) return '❌';
    if (attemptCount === 1) return '✅';
    if (attemptCount === 2) return '2️⃣';
    if (attemptCount === 3) return '3️⃣';
    return '❌';
  }).join('');

  const triviaIndices = Array.from({ length: totalQuestions }, (_, idx) => idx).filter((idx) =>
    isTriviaItem(questions[idx])
  );
  const triviaQuestionCount = triviaIndices.length;

  let rawAccuracy = 0;
  if (triviaQuestionCount > 0) {
    const triviaAttempts = triviaIndices.reduce((sum, idx) => {
      const sequence = guessSequences[idx];
      return sum + (Array.isArray(sequence) ? sequence.length : 0);
    }, 0);
    rawAccuracy = Math.round(
      (1 - Math.max(0, triviaAttempts - triviaQuestionCount) / (triviaQuestionCount * 3)) * 100
    );
  }

  return {
    emoji_results: emojiResults,
    accuracy: Math.max(0, Math.min(100, rawAccuracy)),
    total_questions: totalQuestions,
    xp_earned: computeQuizXpTotal(questions, questionXp),
  };
};

export const useQuizAdapter = (
  slug: string | undefined,
  options?: {
    initialQuiz?: QuizMetadata | null;
    initialQuestions?: QueryQuizQuestion[];
    serverAnalyticsEnabled?: boolean;
    runtimeQuestionFetchEnabled?: boolean;
    authoritativeInitialData?: boolean;
  }
): QuizGameAdapter => {
  const serverAnalyticsEnabled = options?.serverAnalyticsEnabled ?? true;
  const runtimeQuestionFetchEnabled = options?.runtimeQuestionFetchEnabled ?? true;
  const {
    data: queriedQuiz,
    isLoading: isQueriedQuizLoading,
    error: queriedQuizError,
  } = useQuizMetadataQuery(slug, { initialData: options?.initialQuiz });
  const quiz = options?.authoritativeInitialData ? (options.initialQuiz ?? undefined) : queriedQuiz;
  const isQuizLoading = options?.authoritativeInitialData ? false : isQueriedQuizLoading;
  const quizError = options?.authoritativeInitialData ? null : queriedQuizError;
  const quizId = quiz?.quiz_id;
  const editionId = quiz?.edition_id;
  const initialQuestions = editionId ? options?.initialQuestions : undefined;

  const { userId, isAuthenticated, isResolved } = useAuthId();
  const activeRunIdentity = useActiveRunIdentity();
  const queryClient = useQueryClient();
  const { completion, isLoadingCompletion, completionError } = useQuizCompletionQuery(
    quizId,
    editionId,
    { overrideUserId: userId || undefined, enabled: serverAnalyticsEnabled }
  );

  const guessSequencesRef = useRef<number[][]>([]);
  const questionXpRef = useRef<number[]>([]);
  const questionsRef = useRef<QuizQuestion[]>([]);
  const partnerAnalyticsSessionRef = useRef<PartnerAnalyticsSession | undefined>(undefined);
  const [completionResult, setCompletionResult] = useState<DurableQuizCompletion | null>(null);
  const [completionPending, setCompletionPending] = useState(false);
  const [persistenceError, setPersistenceError] = useState<unknown>(null);

  const persistenceIdentity = useMemo(() => {
    if (!quizId || !editionId) return null;
    if (serverAnalyticsEnabled && activeRunIdentity.status !== 'ready') return null;
    const canonicalIdentity =
      activeRunIdentity.identity ?? (userId ? { userId, isAnonymous: !isAuthenticated } : null);
    if (!canonicalIdentity && serverAnalyticsEnabled) return null;
    return {
      quizId,
      editionId,
      userId: canonicalIdentity?.userId ?? `preview:${quizId}`,
      isAnonymous: canonicalIdentity?.isAnonymous ?? true,
      identityGeneration: activeRunIdentity.generation,
    };
  }, [
    activeRunIdentity.generation,
    activeRunIdentity.identity,
    activeRunIdentity.status,
    editionId,
    isAuthenticated,
    quizId,
    serverAnalyticsEnabled,
    userId,
  ]);

  const persistence = useMemo<QuizRunPersistence | null>(() => {
    if (!persistenceIdentity) return null;
    const isCurrent = () => {
      if (serverAnalyticsEnabled) {
        const state = getActiveRunIdentityState();
        return (
          state.status === 'ready' &&
          state.generation === persistenceIdentity.identityGeneration &&
          state.identity?.userId === persistenceIdentity.userId &&
          state.identity?.isAnonymous === persistenceIdentity.isAnonymous
        );
      }
      return true;
    };
    if (!serverAnalyticsEnabled) {
      return createPreviewQuizRunPersistence({
        identity: persistenceIdentity,
        isIdentityCurrent: isCurrent,
      });
    }
    return createQuizRunPersistence({
      identity: persistenceIdentity,
      transport: createQuizRunPersistenceTransport({
        questionIndexForItem: (editionItemId) =>
          questionsRef.current.find((question) => question.edition_item_id === editionItemId)
            ?.question_index,
      }),
      isIdentityCurrent: isCurrent,
      reconcileCompletion: async (durableCompletion) => {
        const identity = persistenceIdentity;
        queryClient.invalidateQueries({ queryKey: ['quizCompletion', quizId, editionId] });
        markGameRunCachesDirty(queryClient, identity.userId);
        void reconcileGameRunCaches(queryClient, identity.userId);
        queryClient.invalidateQueries({ queryKey: socialLeaderboardKeys.all });
        if (quiz?.partner_id) {
          queryClient.invalidateQueries({ queryKey: ['partnerStreakStatus', quiz.partner_id] });
        }
        const partnerAnalyticsSession = partnerAnalyticsSessionRef.current;
        if (partnerAnalyticsSession) {
          void partnerAnalyticsSession.recordActivity({
            quizId: durableCompletion.quizId,
            editionId: durableCompletion.editionId,
            userId: identity.userId,
            isAnonymous: identity.isAnonymous,
            completed: true,
          });
        }
      },
    });
  }, [
    editionId,
    persistenceIdentity,
    queryClient,
    quiz?.partner_id,
    quizId,
    serverAnalyticsEnabled,
  ]);

  const persistenceRef = useRef<QuizRunPersistence | null>(null);
  const pendingPersistenceSnapshotRef = useRef<QuizRunPersistenceSnapshot | null>(null);
  const pendingPersistenceSourceRef = useRef<QuizRunPersistence | null>(null);
  const pendingPersistenceClaimRef = useRef(false);
  const pendingPersistenceInterruptedRef = useRef(false);
  useEffect(() => {
    const pendingSnapshot = pendingPersistenceSnapshotRef.current;
    const pendingSource = pendingPersistenceSourceRef.current;
    const pendingClaim = pendingPersistenceClaimRef.current;
    const pendingInterrupted = pendingPersistenceInterruptedRef.current;

    if (pendingSnapshot && persistence && pendingSource !== persistence) {
      pendingPersistenceSnapshotRef.current = null;
      pendingPersistenceSourceRef.current = null;
      pendingPersistenceClaimRef.current = false;
      pendingPersistenceInterruptedRef.current = false;
      const nextIdentity = persistence.getSnapshot().identity;
      const isGuestClaimRebind = pendingSnapshot.identity.isAnonymous && !nextIdentity.isAnonymous;
      if (
        pendingSnapshot.identity.quizId === nextIdentity.quizId &&
        pendingSnapshot.identity.editionId === nextIdentity.editionId &&
        isGuestClaimRebind &&
        (pendingClaim || !pendingInterrupted)
      ) {
        const migrate = async () => {
          // Rehydrate the replacement owner with the claimed snapshot's
          // revision and acknowledgement watermark before any new feedback
          // staging. This preserves a transferred server revision N so the
          // next local update is revision N+1, rather than restarting at 1.
          const canonicalItems: CanonicalItem[] = pendingSnapshot.items.map((item) =>
            item.itemType === 'trivia'
              ? {
                  editionItemId: item.editionItemId,
                  itemType: 'trivia' as const,
                  fact: {
                    guessSequence: item.guessSequence,
                    rating: item.rating,
                    feedback: item.feedback,
                    feedbackText: item.feedbackText,
                    xpEarned: item.xpEarned,
                    revision: item.revision,
                    acknowledgedRevision: item.acknowledgedRevision,
                  },
                }
              : {
                  editionItemId: item.editionItemId,
                  itemType: 'poll' as const,
                  pollDefinitionId: item.pollDefinitionId,
                  fact: {
                    optionId: item.optionId,
                    sessionId: item.sessionId,
                    xpEarned: item.xpEarned,
                    revision: item.revision,
                    acknowledgedRevision: item.acknowledgedRevision,
                  },
                }
          );
          await persistence.recover(canonicalItems);

          if (pendingSnapshot.completionRequested) await persistence.requestCompletion();
        };
        void migrate().catch(() => undefined);
      }
    } else if (persistence && pendingSource === persistence) {
      // React Strict Mode may replay an effect without changing the owner.
      pendingPersistenceSnapshotRef.current = null;
      pendingPersistenceSourceRef.current = null;
      pendingPersistenceClaimRef.current = false;
      pendingPersistenceInterruptedRef.current = false;
    } else if (!persistence && pendingSnapshot) {
      // A null owner between generations is safe to cross only when the
      // active identity owner explicitly entered a guest-claim transition.
      pendingPersistenceInterruptedRef.current = true;
    }
    persistenceRef.current = persistence;
    return () => {
      if (persistenceRef.current === persistence) persistenceRef.current = null;
      if (persistence) {
        const snapshot = persistence.getSnapshot();
        const activeIdentityState = getActiveRunIdentityState();
        pendingPersistenceSnapshotRef.current = snapshot;
        pendingPersistenceSourceRef.current = persistence;
        pendingPersistenceClaimRef.current =
          activeIdentityState.status === 'claiming' &&
          snapshot.identity.isAnonymous &&
          activeIdentityState.identity?.userId === snapshot.identity.userId;
        pendingPersistenceInterruptedRef.current = false;
        persistence.dispose(pendingPersistenceClaimRef.current ? 'owner_replaced' : 'disposed');
      }
    };
  }, [persistence]);

  const useQuestions = useCallback(
    function useQuestions(): BaseQuestionsResult {
      const {
        data: questions = [],
        isLoading: isQuestionsLoading,
        isPending: isQuestionsPending,
        isProgressReady,
        error: questionsError,
      } = useQuizQuestionsQuery(quizId, editionId, {
        initialData: initialQuestions,
        enabled: runtimeQuestionFetchEnabled,
      });
      const effectiveQuestions = options?.authoritativeInitialData
        ? (initialQuestions ?? [])
        : questions;
      const progressReady = isProgressReady ?? true;
      const hasSettledQuestions = progressReady && effectiveQuestions.length > 0;
      const effectiveLoading = options?.authoritativeInitialData
        ? false
        : !questionsError && (isQuestionsLoading || !!isQuestionsPending || !progressReady);
      const effectiveError =
        options?.authoritativeInitialData || hasSettledQuestions
          ? null
          : ((questionsError as Error | null) ?? null);

      questionsRef.current = effectiveQuestions as QuizQuestion[];
      return {
        questions: effectiveQuestions as unknown as Question[],
        loading: effectiveLoading,
        error: effectiveError,
      };
    },
    [
      editionId,
      initialQuestions,
      options?.authoritativeInitialData,
      quizId,
      runtimeQuestionFetchEnabled,
    ]
  );

  useEffect(() => {
    guessSequencesRef.current = [];
    questionXpRef.current = [];
    setCompletionResult(null);
    setPersistenceError(null);
  }, [quizId, editionId]);

  const canonicalItems = useCallback((questions: QuizQuestion[]): CanonicalItem[] => {
    return questions.flatMap<CanonicalItem>((question) => {
      if (!question.edition_item_id) return [];
      if (isPollItem(question)) {
        const pollResponse = question.poll_response;
        const persistenceRevision = pollResponse?.persistenceRevision;
        const progress = Array.isArray(question.item_progress_guess_sequence)
          ? {
              editionItemId: question.edition_item_id,
              guessSequence: question.item_progress_guess_sequence,
            }
          : undefined;
        return [
          {
            editionItemId: question.edition_item_id,
            itemType: 'poll' as const,
            pollDefinitionId: question.poll_definition_id ?? '',
            progress,
            fact: pollResponse?.selectedOptionId
              ? {
                  optionId: pollResponse.selectedOptionId,
                  sessionId: pollResponse.sessionId,
                  xpEarned: pollResponse.xpEarned ?? question.xp_earned,
                  revision: persistenceRevision,
                  acknowledgedRevision: persistenceRevision,
                }
              : undefined,
          },
        ];
      }
      return [
        {
          editionItemId: question.edition_item_id,
          itemType: 'trivia' as const,
          progress: Array.isArray(question.item_progress_guess_sequence)
            ? {
                editionItemId: question.edition_item_id,
                guessSequence: question.item_progress_guess_sequence,
              }
            : undefined,
          fact:
            Array.isArray(question.guess_sequence) && question.guess_sequence.length > 0
              ? {
                  guessSequence: question.guess_sequence,
                  xpEarned: question.xp_earned,
                  rating: question.rating,
                  feedback: question.feedback,
                  feedbackText: question.feedback_text,
                  revision: question.persistence_revision,
                  acknowledgedRevision: question.persistence_revision,
                }
              : undefined,
        },
      ];
    });
  }, []);

  const logAnswer = useCallback(
    async (data: LogAnswerData) => {
      if (!isResolved) return;
      const question = questionsRef.current.find(
        (candidate) => candidate.question_id === data.questionId
      );
      if (!question || isPollItem(question) || !question.edition_item_id || !persistence) return;
      const questionIndex = question.question_index;
      const nextSequences = [...guessSequencesRef.current];
      nextSequences[questionIndex] = [...data.guessSequence];
      guessSequencesRef.current = nextSequences;
      const rawXpEarned =
        data.netXP ?? questionXpRef.current[questionIndex] ?? question.xp_earned ?? 0;
      // The durable Quiz contract stores bounded non-negative XP. Mechanics
      // can temporarily carry a negative net total after haste penalties;
      // clamp at this persistence boundary so that penalty never drops the
      // answer snapshot itself.
      const xpEarned = Math.min(QUIZ_PERSISTED_XP_MAX, Math.max(0, rawXpEarned));
      const nextXp = [...questionXpRef.current];
      nextXp[questionIndex] = xpEarned;
      questionXpRef.current = nextXp;
      partnerAnalyticsSessionRef.current = data.partnerAnalyticsSession;
      try {
        await persistence.stageTrivia({
          editionItemId: question.edition_item_id,
          guessSequence: data.guessSequence,
          rating: data.rating ?? null,
          feedback: data.feedback ?? null,
          feedbackText: data.feedback_text ?? null,
          xpEarned,
        });
      } catch (error) {
        if (isExpectedQuizRunPersistenceRetirement(error)) return;
        throw error;
      }
      if (serverAnalyticsEnabled && data.partnerAnalyticsSession) {
        const identity = persistence.getSnapshot().identity;
        void data.partnerAnalyticsSession.recordActivity({
          quizId: identity.quizId,
          editionId: identity.editionId,
          userId: identity.userId,
          isAnonymous: identity.isAnonymous,
        });
      }
    },
    [isResolved, persistence, serverAnalyticsEnabled]
  );

  const stagePoll = useCallback(
    async (input: {
      editionItemId: string;
      pollDefinitionId: string;
      optionId: string;
      sessionId?: string | null;
    }): Promise<PollResponseResult> => {
      const question = questionsRef.current.find(
        (candidate) => candidate.edition_item_id === input.editionItemId
      );
      if (!persistence || !question) {
        return { alreadyAnswered: false, xpAwarded: POLL_PARTICIPATION_XP, distribution: [] };
      }
      const result = await persistence.stagePoll(input);
      if (result && typeof result === 'object' && 'xpAwarded' in result) {
        return result as PollResponseResult;
      }
      return {
        alreadyAnswered: false,
        xpAwarded: POLL_PARTICIPATION_XP,
        // Preview has no server results. Missing counts must stay unavailable,
        // rather than being presented as measured zero-percent responses.
        distribution: [],
      };
    },
    [persistence]
  );

  const recordItemProgress = useCallback(
    async (input: { editionItemId: string; guessSequence: readonly number[] }) => {
      if (!persistence || !serverAnalyticsEnabled) return;
      try {
        await persistence.recordItemProgress(input);
      } catch (error) {
        if (!isExpectedQuizRunPersistenceRetirement(error)) throw error;
      }
    },
    [persistence, serverAnalyticsEnabled]
  );

  const trackQuestionXp = useCallback((questionIndex: number, xp: number) => {
    const nextXp = [...questionXpRef.current];
    nextXp[questionIndex] = xp;
    questionXpRef.current = nextXp;
  }, []);

  const completeRun = useCallback(
    async (questions: QuizQuestion[]): Promise<DurableQuizCompletion | null> => {
      if (!persistence) return null;
      setCompletionPending(true);
      try {
        // Both gameplay completion and completed hydration need the canonical
        // Edition set reconciled before asking the durable finalizer to run.
        await persistence.recover(canonicalItems(questions));
        const result = await persistence.requestCompletion();
        if (result) setCompletionResult(result);
        return result;
      } catch (error) {
        if (isExpectedQuizRunPersistenceRetirement(error)) return null;
        setPersistenceError(error);
        throw error;
      } finally {
        setCompletionPending(false);
      }
    },
    [canonicalItems, persistence]
  );
  const requestCompletion = useCallback(() => completeRun(questionsRef.current), [completeRun]);

  const completedHydrationRef = useRef<{
    owner: QuizRunPersistence;
    recovery: Promise<void>;
  } | null>(null);
  const recoverRun = useCallback(
    async (questions: Question[]) => {
      if (!persistence || questions.length === 0) return;
      const quizQuestions = questions as QuizQuestion[];
      const allAnswered = quizQuestions.every((question) =>
        isPollItem(question)
          ? !!question.is_answered || !!question.poll_response?.selectedOptionId
          : Array.isArray(question.guess_sequence) && question.guess_sequence.length > 0
      );
      if (allAnswered) {
        if (completedHydrationRef.current?.owner === persistence) {
          return completedHydrationRef.current.recovery;
        }
        // Bind the in-flight recovery before another hydration signal arrives.
        // The persistence owner already fences the canonical identity generation.
        const recovery = completeRun(quizQuestions).then(() => undefined);
        completedHydrationRef.current = { owner: persistence, recovery };
        return recovery;
      }

      try {
        await persistence.recover(canonicalItems(quizQuestions));
        if (completedHydrationRef.current?.owner === persistence) {
          completedHydrationRef.current = null;
        }
      } catch (error) {
        if (!isExpectedQuizRunPersistenceRetirement(error)) throw error;
      }
    },
    [canonicalItems, completeRun, persistence]
  );

  const adapterStateRef = useRef({
    loading: isQuizLoading,
    error: (quizError as Error | null) ?? null,
    completion,
    completionResult,
    isCompletionLoading: isLoadingCompletion || completionPending,
    completionError: persistenceError ?? completionError,
    quiz: quiz ?? null,
    isAuthenticated,
  });
  adapterStateRef.current = {
    loading: isQuizLoading,
    error: (quizError as Error | null) ?? null,
    completion,
    completionResult,
    isCompletionLoading: isLoadingCompletion || completionPending,
    completionError: persistenceError ?? completionError,
    quiz: quiz ?? null,
    isAuthenticated,
  };

  return useMemo(
    () => ({
      mode: 'quiz' as const,
      useQuestions,
      logAnswer,
      recordItemProgress,
      trackQuestionXp,
      stagePoll,
      recoverRun,
      requestCompletion,
      getCompletion: () => {
        const { completion: queriedCompletion, completionResult: durable } =
          adapterStateRef.current;
        const durableCompletion = durable ?? persistence?.getSnapshot().completion ?? null;
        const completionFacts = resolveQuizCompletionFacts(
          questionsRef.current,
          guessSequencesRef.current,
          questionXpRef.current,
          adapterStateRef.current.quiz?.num_questions
        );
        const fallbackCompletion = computeLocalQuizCompletion(
          completionFacts.guessSequences,
          completionFacts.totalQuestions,
          questionsRef.current,
          questionXpRef.current
        );
        if (durableCompletion) {
          return {
            emojiResults: durableCompletion.emojiResults,
            accuracy: durableCompletion.accuracy,
            totalQuestions: durableCompletion.totalQuestions,
            xpEarned: durableCompletion.xpEarned,
          };
        }
        const resolvedCompletion = queriedCompletion ?? fallbackCompletion;
        return resolvedCompletion
          ? {
              emojiResults: resolvedCompletion.emoji_results,
              accuracy: resolvedCompletion.accuracy,
              totalQuestions: resolvedCompletion.total_questions,
              xpEarned: resolvedCompletion.xp_earned ?? completionFacts.xpEarned,
            }
          : null;
      },
      get isCompletionLoading() {
        return adapterStateRef.current.isCompletionLoading;
      },
      get completionMutationError() {
        return adapterStateRef.current.completionError;
      },
      get hasPersistedCompletion() {
        return !!(adapterStateRef.current.completion || persistence?.getSnapshot().completion);
      },
      get serverAnalyticsEnabled() {
        return serverAnalyticsEnabled;
      },
      get canPersistCompletion() {
        return serverAnalyticsEnabled;
      },
      sharePayload: () => {
        const { completion: c, completionResult: durable, quiz: q } = adapterStateRef.current;
        if (!q) return null;
        return durable || c
          ? {
              slug: q.slug,
              title: q.title,
              emojiResults: durable?.emojiResults ?? c?.emoji_results ?? '',
              accuracy: durable?.accuracy ?? c?.accuracy ?? 0,
            }
          : null;
      },
      get quiz() {
        return adapterStateRef.current.quiz;
      },
      get isLoading() {
        return adapterStateRef.current.loading;
      },
      get error() {
        return adapterStateRef.current.error;
      },
      get hasAuthenticatedUser() {
        return adapterStateRef.current.isAuthenticated;
      },
    }),
    [
      logAnswer,
      persistence,
      recordItemProgress,
      recoverRun,
      requestCompletion,
      serverAnalyticsEnabled,
      stagePoll,
      trackQuestionXp,
      useQuestions,
    ]
  );
};
